import React from "react";
import type { ProjectName } from "@neontree/bible-api-client";
import type { CancellationReason } from "pages/UnsubscribePage";

export interface BrandConfig {
  /**
   * Which product this app is, as known by the core API. Sent as the
   * `x-nt-project` header on every request; scopes subscriptions, magic-link
   * branding and consent. See `configureCoreAPI`.
   */
  projectName: ProjectName;
  /** Display name of the product (e.g. "Lovelica") */
  name: string;
  /** Support / contact email */
  email: string;
  /** Canonical web app URL */
  url: string;
  /** Logo image — import the PNG at the brand-app level and pass it here */
  logo: string;
  /** Links shown in footer and settings */
  docLinks: {
    termsUrl: string;
    privacyUrl: string;
  };
  /**
   * Bump when consent wording or legal basis changes.
   * Users who consented to an older version must re-consent.
   */
  ageConsentVersion: string;
  /** Reasons shown in the cancellation dialog on the unsubscribe page */
  cancellationReasons: CancellationReason[];
}

const BrandContext = React.createContext<BrandConfig | null>(null);

export function BrandProvider({
  brand,
  children,
}: {
  brand: BrandConfig;
  children: React.ReactNode;
}) {
  return (
    <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
  );
}

export function useBrand(): BrandConfig {
  const ctx = React.useContext(BrandContext);
  if (!ctx) {
    throw new Error("useBrand must be used within BrandProvider");
  }
  return ctx;
}
